import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'

import Footer from '@/components-latest/layout/Footer/Footer'
import { SmallFooter } from '@/components-latest/layout/SmallFooter/SmallFooter'
import { ArrowLeft } from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'
import DirectionProvider from '@/components-latest/shared/DirectionProvider'
import EmailVerificationCard from '@/components-latest/ui/RegistrationPage/EmailVerificationCard'
import { useRegisterStoreMutation } from '@/ducks/registration/api/registration.api'
import { useToaster } from '@/components-latest/ui/Toaster/useToaster/useToaster'
import { RegistrationFormData } from '@/components-latest/ui/RegistrationPage/RegistrationForm'

import Styles from './EmailVerificationPage.module.scss'

interface LocationState {
  email: string
  userName?: string
  registrationData: RegistrationFormData
}

const EmailVerificationPage: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { addToast } = useToaster()

  const [registerStore] = useRegisterStoreMutation()

  const state = location.state as LocationState | null

  // Redirect if no state data
  useEffect(() => {
    if (!state?.email) {
      history.replace('/registration')
    }
  }, [state, history])

  const handleVerificationSuccess = useCallback(async () => {
    if (!state?.registrationData) return

    // After verification succeeds, complete the registration
    const DTO = {
      email: state.registrationData.email,
      phone: state.registrationData.phone,
      address: {
        lng: state.registrationData.address.coords.lng,
        lat: state.registrationData.address.coords.lat,
        line1: state.registrationData.address.fullAddress,
        city: state.registrationData.address.city,
        state: state.registrationData.address.state,
        country: state.registrationData.address.country,
        country_code: state.registrationData.address.countryCode
      },
      resname: state.registrationData.restaurantName,
      username: state.registrationData.username,
      main_city: state.registrationData.citySelect,
      main_cuisine: state.registrationData.cuisines.map(cuisine => ({
        _id: cuisine.value,
        name: cuisine.label
      })),
      password: state.registrationData.password,
      cpwd: state.registrationData.confirmPassword,
      docs: [],
      documents: [],
      documentsNotRequired: [],
      role: 'restaurant'
    }

    try {
      const result = await registerStore(DTO).unwrap()
      history.replace('/registration', {
        verified: true,
        registrationData: result
      })
    } catch (err: unknown) {
      const error = err as { message?: string }
      addToast({
        type: 'system',
        intent: 'error',
        message: error?.message || t('registration.page.registrationFailed')
      })
    }
  }, [state, registerStore, history, addToast, t])

  if (!state?.email) {
    return null
  }

  const handleGoBack = useCallback(() => {
    history.goBack()
  }, [history])

  return (
    <DirectionProvider>
      <div className={Styles.emailVerificationPage} data-testid="email-verification-page">
        <button
          type="button"
          onClick={handleGoBack}
          className={Styles.backButton}
          aria-label="Go back">
          <ArrowLeft className={Styles.backIcon} />
        </button>

        <Link to="/registration" className={Styles.logoWrapper}>
          <img
            className={Styles.logo}
            alt="logo"
            src="/images/yassir-express-text-logo.svg"
          />
        </Link>

        <div className={Styles.content}>
          <EmailVerificationCard
            email={state.email}
            phone={state.registrationData.phone}
            userName={state.userName}
            trackingData={{
              restaurant_name: state?.registrationData?.restaurantName || '',
              city: state?.registrationData?.citySelect || '',
              country: state?.registrationData?.countrySelect || '',
              username: state?.registrationData?.username || ''
            }}
            onVerificationSuccess={handleVerificationSuccess}
          />
        </div>
      </div>
      <Footer />
      <SmallFooter />
    </DirectionProvider>
  )
}

export default EmailVerificationPage
